import {
  MediaRecorderAudioChunk,
  MediaRecorderEngine,
  MediaRecorderEventHandlers,
  MediaRecorderMimeType,
} from "@/store/audio/recording/media-types";
import { AudioRecordingErrorStage } from "@/domains/errors";
import { DesktopPcmChunkAccumulator } from "@/store/audio/recording/media-recorder/desktop-pcm-chunk-accumulator";
import { DesktopCaptureService } from "@/modules/audio-recording/capture";
import { audioRecordingModule } from "@/modules/audio-recording";

const DEFAULT_CHUNK_INTERVAL_MS = 1000;

export class DesktopMediaRecorder implements MediaRecorderEngine {
  private readonly captureService: DesktopCaptureService;
  private readonly sampleRate: number;
  private readonly chunkAccumulator: DesktopPcmChunkAccumulator;
  private readonly emitError: (error: unknown, stage?: AudioRecordingErrorStage) => void;

  private handlers: MediaRecorderEventHandlers = {};
  private isRecording = false;

  constructor({
    captureService,
    sampleRate,
    chunkIntervalMs = DEFAULT_CHUNK_INTERVAL_MS,
    emitError,
  }: {
    captureService: DesktopCaptureService;
    sampleRate: number;
    chunkIntervalMs?: number;
    emitError: (error: unknown, stage?: AudioRecordingErrorStage) => void;
  }) {
    this.captureService = captureService;
    this.sampleRate = sampleRate;
    this.emitError = emitError;
    this.chunkAccumulator = new DesktopPcmChunkAccumulator({
      sampleRate,
      chunkDurationMs: chunkIntervalMs,
    });
  }

  setEventHandlers(handlers: MediaRecorderEventHandlers): void {
    this.handlers = handlers ?? {};
  }

  async start(abortSignal?: AbortSignal): Promise<void> {
    try {
      this.chunkAccumulator.reset();
      await this.captureService.start(abortSignal);
      if (abortSignal?.aborted) return;

      this.isRecording = true;
      this.handlers.onStart?.();
    } catch (error) {
      this.isRecording = false;
      throw error;
    }
  }

  attachRecoveredSession(): void {
    this.chunkAccumulator.reset();
    this.isRecording = true;
  }

  async stop(): Promise<void> {
    try {
      const remainder = this.chunkAccumulator.flush();
      if (remainder) {
        this.emitChunk(remainder);
      }

      await this.captureService.stop();
    } catch (error) {
      this.emitError(error, AudioRecordingErrorStage.CaptureStop);
    }

    this.chunkAccumulator.reset();
    this.isRecording = false;

    this.handlers.onStop?.();
  }

  async pause(): Promise<void> {
    try {
      await this.captureService.pause();
    } catch (error) {
      this.emitError(error, AudioRecordingErrorStage.CapturePause);
    }
  }

  async resume(): Promise<void> {
    try {
      await this.captureService.resume();
    } catch (error) {
      this.emitError(error, AudioRecordingErrorStage.CaptureResume);
    }
  }

  dispose(): void {
    this.isRecording = false;
    this.chunkAccumulator.reset();
    void this.captureService.cancel();
  }

  handleSamples(samples: Int16Array): void {
    if (!this.isRecording) return;
    this.processSamples(samples);
  }

  private processSamples(samples: Int16Array): void {
    const chunks = this.chunkAccumulator.push(samples);
    for (const chunkSamples of chunks) {
      this.emitChunk(chunkSamples);
    }
  }

  private emitChunk(samples: Int16Array): void {
    const chunk: MediaRecorderAudioChunk = audioRecordingModule.buildRecorderChunkFromPcm({
      samples,
      sampleRate: this.sampleRate,
      mimeType: MediaRecorderMimeType.Pcm16,
    });

    try {
      this.handlers.onAudioChunk?.({ chunk });
    } catch (error) {
      this.emitError(error, AudioRecordingErrorStage.EmitChunk);
    }
  }
}
